/**
 * The four directions an arrow may point. Kept as a string union so that serialized
 * level data stays human readable; hot loops convert to the numeric index below.
 */
export type Direction = 'up' | 'right' | 'down' | 'left';

/** Canonical ordering. Never reorder: the index is part of the serialization format. */
export const DIRECTIONS: readonly Direction[] = ['up', 'right', 'down', 'left'] as const;

export const DIRECTION_INDEX: Readonly<Record<Direction, number>> = {
  up: 0,
  right: 1,
  down: 2,
  left: 3,
};

export interface DirectionVector {
  readonly dx: number;
  readonly dy: number;
}

export const DIRECTION_VECTORS: Readonly<Record<Direction, DirectionVector>> = {
  up: { dx: 0, dy: -1 },
  right: { dx: 1, dy: 0 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
};

/** Rotation in radians used by the renderer, relative to an "up" facing sprite. */
export const DIRECTION_ROTATION: Readonly<Record<Direction, number>> = {
  up: 0,
  right: Math.PI / 2,
  down: Math.PI,
  left: -Math.PI / 2,
};

export function directionFromIndex(index: number): Direction {
  const direction = DIRECTIONS[index];
  if (direction === undefined) {
    throw new RangeError(`Invalid direction index: ${index}`);
  }
  return direction;
}

export function isDirection(value: unknown): value is Direction {
  return value === 'up' || value === 'right' || value === 'down' || value === 'left';
}

export function oppositeDirection(direction: Direction): Direction {
  switch (direction) {
    case 'up':
      return 'down';
    case 'down':
      return 'up';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
  }
}

/** True when the direction moves along a row (horizontal), false for columns. */
export function isHorizontal(direction: Direction): boolean {
  return direction === 'left' || direction === 'right';
}

/** True when the direction moves towards increasing x or y. */
export function isPositive(direction: Direction): boolean {
  return direction === 'right' || direction === 'down';
}
